import { css, cx } from "styled-system/css";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import type { AppointmentStatus, PaymentStatus } from "@prisma/client";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { Clock, CalendarCheck, AlertTriangle } from "lucide-react";
import { animate, motion, useMotionValue, useMotionValueEvent, useReducedMotion } from "motion/react";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { OnboardingChecklistBanner } from "@/components/dashboard/onboarding-checklist-banner";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import type { TrendDelta } from "@/components/dashboard/dashboard-kpi-animated-card";
import { DashboardRecentActivityTable, type RecentActivityRow } from "@/components/dashboard/dashboard-recent-activity-table";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

type Period = "week" | "month" | "year";

type AnalyticsSummary = {
  timezone: string;
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  noShowAppointments: number;
  noShowRate: number;
  totalRevenue: number;
  pendingRevenue: number;
  depositRevenue: number;
  newClients: number;
  returningClients: number;
  avgAppointmentsPerDay: number;
  revenueChange: TrendDelta;
  bookingsChange: TrendDelta;
};

type TrendItem = { date: string; count: number; revenue: number };
type ServiceItem = { serviceId: string; serviceName: string; count: number; revenue: number; noShowRate: number };

type RecentAppointment = {
  id: string;
  createdAt: string;
  startTime: string;
  endTime: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string | null;
  status: AppointmentStatus;
  paymentStatus: PaymentStatus;
  depositStatus: string | null;
  depositAmount: number | null;
  depositPaidAt: string | null;
  service: { id: string; name: string; price: number | null } | null;
  professional?: { name: string };
};

function depositOrFullRevenue(a: RecentAppointment) {
  const dep = a.depositStatus === "paid" ? a.depositAmount ?? 0 : 0;
  const full = a.paymentStatus === "PAID" ? a.service?.price ?? 0 : 0;
  return dep + full;
}

const PEAK_DAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function formatPeakLabel(p: { dayOfWeek: number; hour: number }) {
  const d = PEAK_DAY_LABELS[p.dayOfWeek] ?? "—";
  return `${d} ${String(p.hour).padStart(2, "0")}:00`;
}

const PERIODS: { value: Period; label: string }[] = [
  { value: "week", label: "Esta semana" },
  { value: "month", label: "Este mes" },
  { value: "year", label: "Este año" },
];

function untilLabel(minutes: number): string {
  if (minutes < 1) return "ahora mismo";
  if (minutes < 60) return `en ${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `en ${h}h ${m}m` : `en ${h}h`;
}

function StatCard({ label, value, sub, dim }: { label: string; value: string; sub?: string; dim?: boolean }) {
  return (
    <div className={cx(cf.dashSurface, css({ p: "5" }))}>
      <p className={css({
        fontSize: "11px",
        fontWeight: "600",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#94a3b8",
        mb: "2",
      })}>
        {label}
      </p>
      <p className={css({
        fontSize: "2xl",
        fontWeight: "800",
        color: dim ? "#cbd5e1" : "#0f172a",
        letterSpacing: "-0.02em",
        lineHeight: "1",
      })}>
        {value}
      </p>
      {sub ? (
        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1.5" })}>{sub}</p>
      ) : null}
    </div>
  );
}

function DeltaBadge({ delta }: { delta: TrendDelta }) {
  if (delta.type === "dash") {
    return <span className={css({ fontSize: "xs", color: "#94a3b8", fontWeight: "semibold" })}>—</span>;
  }
  if (delta.type === "new") {
    return <span className={css({ fontSize: "xs", color: "#166534", fontWeight: "semibold" })}>Nuevo</span>;
  }
  const v = delta.value;
  const color = v > 0 ? "#166534" : v < 0 ? "#b91c1c" : "#64748b";
  return (
    <span className={css({ fontSize: "xs", fontWeight: "semibold" })} style={{ color }}>
      {v > 0 ? "+" : ""}{v}%
    </span>
  );
}

function FeaturedKpiCard({
  label,
  target,
  animationKey,
  prefix = "",
  sub,
  changeDelta,
  staggerIndex = 0,
}: {
  label: string;
  target: number;
  animationKey: string;
  prefix?: string;
  sub?: string;
  changeDelta?: TrendDelta;
  staggerIndex?: 0 | 1;
}) {
  const mv = useMotionValue(0);
  const [n, setN] = useState(0);
  const shouldReduceMotion = useReducedMotion();

  useMotionValueEvent(mv, "change", (latest) => setN(Math.round(latest)));

  useEffect(() => {
    if (shouldReduceMotion) { setN(target); return; }
    mv.set(0);
    const c = animate(mv, target, { duration: 0.7, ease: [0.23, 1, 0.32, 1] });
    return () => c.stop();
  }, [target, animationKey, mv, shouldReduceMotion]);

  return (
    <motion.div
      initial={shouldReduceMotion ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: staggerIndex * 0.06, duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className={cx(cf.dashSurface, css({ p: "6" }))}
    >
      <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "3" })}>
        <p className={css({ fontSize: "11px", fontWeight: "600", letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8" })}>
          {label}
        </p>
        {changeDelta ? <DeltaBadge delta={changeDelta} /> : null}
      </div>
      <p className={css({ fontSize: "4xl", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.04em", lineHeight: "1" })}>
        {prefix}{n}
      </p>
      {sub ? (
        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "2" })}>{sub}</p>
      ) : null}
    </motion.div>
  );
}

export function DashboardHome() {
  const { refresh } = useAuth();
  const shouldReduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const agendaIsProfessional = Boolean(searchParams.get("professionalId"));
  const [period, setPeriod] = useState<Period>("month");
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [trend, setTrend] = useState<TrendItem[] | null>(null);
  const [byService, setByService] = useState<ServiceItem[] | null>(null);
  const [peakHours, setPeakHours] = useState<{ dayOfWeek: number; hour: number; count: number }[] | null>(null);
  const [recent, setRecent] = useState<RecentAppointment[] | null>(null);
  const [timezone, setTimezone] = useState<string>("America/Argentina/Buenos_Aires");
  const [analyticsError, setAnalyticsError] = useState(false);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const [baseReady, setBaseReady] = useState(false);
  const [baseError, setBaseError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setBaseReady(false);
      setBaseError(false);
      const [rSet, rAppts] = await Promise.all([
        fetchWithSessionSync("/api/dashboard/settings", { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/dashboard/appointments", { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (rSet.ok) {
        const s = (await rSet.json()) as { timezone: string };
        setTimezone(s.timezone);
      }
      if (rAppts.ok) {
        setRecent((await rAppts.json()) as RecentAppointment[]);
      } else {
        setRecent([]);
      }
      setBaseError(!rSet.ok && !rAppts.ok);
      setBaseReady(true);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    setAnalyticsError(false);
    setAnalyticsReady(false);
    setSummary(null);
    setTrend(null);
    setByService(null);
    setPeakHours(null);
    void (async () => {
      const qs = new URLSearchParams({ period });
      const [rSum, rTrend, rSvc, rPeak] = await Promise.all([
        fetchWithSessionSync(`/api/analytics/summary?${qs.toString()}`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/analytics/appointments-trend?${qs.toString()}&granularity=day`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/analytics/by-service?${qs.toString()}`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/analytics/peak-hours?${qs.toString()}`, { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      if (!rSum.ok) {
        setAnalyticsError(true);
        setAnalyticsReady(true);
        return;
      }
      if (rTrend.ok) {
        const j = (await rTrend.json()) as { items: TrendItem[] };
        if (!cancelled) setTrend(j.items);
      } else if (!cancelled) {
        setTrend([]);
      }
      if (rSvc.ok) {
        const j = (await rSvc.json()) as { items: ServiceItem[] };
        if (!cancelled) setByService(j.items);
      } else if (!cancelled) {
        setByService([]);
      }
      const sum = (await rSum.json()) as AnalyticsSummary;
      if (cancelled) return;
      setSummary(sum);
      if (rPeak.ok) {
        const j = (await rPeak.json()) as { items: { dayOfWeek: number; hour: number; count: number }[] };
        setPeakHours(j.items.slice(0, 10));
      } else {
        setPeakHours([]);
      }
      setAnalyticsReady(true);
    })();
    return () => { cancelled = true; };
  }, [period, refresh]);

  const todayStats = useMemo(() => {
    if (!recent) return null;
    const now = new Date();
    const todayYmd = formatInTimeZone(now, timezone, "yyyy-MM-dd");
    const today = recent.filter(
      (a) => formatInTimeZone(new Date(a.startTime), timezone, "yyyy-MM-dd") === todayYmd,
    );
    const confirmed = today.filter((a) => a.status === "CONFIRMED").length;
    const pending = today.filter((a) => a.status === "PENDING").length;
    const revenue = today.reduce((acc, a) => acc + depositOrFullRevenue(a), 0);
    const next = recent
      .filter(
        (a) =>
          new Date(a.startTime).getTime() >= now.getTime() &&
          a.status !== "CANCELLED" &&
          a.status !== "NO_SHOW",
      )
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
    return {
      confirmed,
      pending,
      revenue,
      next: next
        ? {
            clientName: next.clientName,
            serviceName: next.service?.name ?? "—",
            timeLabel: formatInTimeZone(new Date(next.startTime), timezone, "HH:mm"),
            minutesUntil: Math.max(0, Math.round((new Date(next.startTime).getTime() - now.getTime()) / 60000)),
          }
        : null,
    };
  }, [recent, timezone]);

  const lineChart = useMemo(() => {
    if (!trend) return null;
    const labels = trend.map((t) => t.date.slice(5) ?? t.date);
    const data = trend.map((t) => Math.round(Number(t.count)) || 0);
    return { labels, data };
  }, [trend]);

  const topServices = useMemo(() => (byService ? byService.slice(0, 5) : []), [byService]);

  const lineChartYMax = useMemo(() => {
    if (!lineChart?.data.length) return 4;
    const m = Math.max(0, ...lineChart.data);
    return Math.max(4, Math.ceil(m) + 1);
  }, [lineChart]);

  const barChartXMax = useMemo(() => {
    if (!topServices.length) return 4;
    const m = Math.max(0, ...topServices.map((s) => s.count));
    return Math.max(4, Math.ceil(m) + 1);
  }, [topServices]);

  const lineOptions: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 10,
          cornerRadius: 8,
          titleColor: "#94a3b8",
          bodyColor: "white",
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: lineChartYMax,
          grid: { color: "rgba(226,232,240,0.6)" },
          ticks: { color: "#94a3b8", stepSize: 1, precision: 0, font: { size: 11 } },
        },
        x: {
          grid: { display: false },
          ticks: { color: "#94a3b8", font: { size: 11 } },
        },
      },
    }),
    [lineChartYMax],
  );

  const barOptions: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 10,
          cornerRadius: 8,
          titleColor: "#94a3b8",
          bodyColor: "white",
        },
      },
      indexAxis: "y",
      scales: {
        x: {
          beginAtZero: true,
          min: 0,
          max: barChartXMax,
          grid: { color: "rgba(226,232,240,0.6)" },
          ticks: { color: "#94a3b8", stepSize: 1, precision: 0, font: { size: 11 } },
        },
        y: {
          grid: { display: false },
          ticks: { color: "#334155", font: { size: 12 } },
        },
      },
    }),
    [barChartXMax],
  );

  const noShowTone = (rate: number) => {
    if (rate < 10) return { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
    if (rate <= 25) return { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" };
    return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
  };

  if (baseError) {
    return (
      <DashboardPageShell eyebrow="Panel" title="Dashboard">
        <p className={css({ fontSize: "sm", color: "#64748b", lineHeight: "1.6" })}>
          No pudimos cargar tu agenda. Probá recargar la página.
        </p>
      </DashboardPageShell>
    );
  }

  if (!baseReady || recent === null) {
    return (
      <DashboardPageShell eyebrow="Panel" title="Dashboard">
        <Skeleton variant="card" className={css({ mb: "5", minH: "100px" })} />
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", md: "repeat(3, 1fr)" },
            gap: "4",
            mb: "8",
          })}
        >
          <Skeleton variant="card" />
          <Skeleton variant="card" />
          <Skeleton variant="card" />
        </div>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", md: "1fr 1fr" },
            gap: "4",
            mb: "4",
          })}
        >
          <Skeleton variant="card" className={css({ minH: "120px" })} />
          <Skeleton variant="card" className={css({ minH: "120px" })} />
        </div>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", lg: "1fr 1fr" },
            gap: "5",
          })}
        >
          <Skeleton variant="card" className={css({ minH: "280px" })} />
          <Skeleton variant="card" className={css({ minH: "280px" })} />
        </div>
      </DashboardPageShell>
    );
  }

  if (!todayStats) return null;

  const dailyRevenue = `$${todayStats.revenue.toFixed(0)}`;
  const peakTopLabel = peakHours && peakHours.length > 0 ? formatPeakLabel(peakHours[0]) : "—";
  const recent10 = [...recent]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 10);

  const analyticsSkeleton = (
    <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", md: "1fr 1fr" },
          gap: "4",
        })}
      >
        <Skeleton variant="card" className={css({ minH: "120px" })} />
        <Skeleton variant="card" className={css({ minH: "120px" })} />
      </div>
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: "3",
        })}
      >
        <Skeleton variant="card" />
        <Skeleton variant="card" />
        <Skeleton variant="card" />
        <Skeleton variant="card" />
      </div>
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", lg: "1fr 1fr" },
          gap: "5",
        })}
      >
        <Skeleton variant="card" className={css({ minH: "280px" })} />
        <Skeleton variant="card" className={css({ minH: "280px" })} />
      </div>
    </div>
  );

  return (
    <DashboardPageShell eyebrow="Panel" title="Dashboard">

      <OnboardingChecklistBanner />

      {/* ── Próximo turno — hero ─────────────────────────── */}
      {todayStats.next ? (
        <div className={cx(cf.dashSurface, css({ overflow: "hidden", mb: "5" }))}>
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", sm: "auto 1fr" },
            })}
          >
            <div
              className={css({
                p: "6",
                borderRightWidth: { sm: "1px" },
                borderColor: "#f1f5f9",
                borderBottomWidth: { base: "1px", sm: "0" },
                minW: { sm: "164px" },
              })}
            >
              <p className={css({ fontSize: "xs", color: "#94a3b8", mb: "3", letterSpacing: "0.04em" })}>
                próximo turno
              </p>
              <p
                style={{
                  fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
                  fontWeight: 800,
                  letterSpacing: "-0.05em",
                  lineHeight: 1,
                  color: "#0f172a",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {todayStats.next.timeLabel}
              </p>
            </div>

            <div
              className={css({
                p: "6",
                display: "flex",
                flexDir: "column",
                justifyContent: "center",
                gap: "0.5",
              })}
            >
              <p
                className={css({
                  fontSize: "lg",
                  fontWeight: "700",
                  color: "#0f172a",
                  letterSpacing: "-0.01em",
                })}
              >
                {todayStats.next.clientName}
              </p>
              <p className={css({ fontSize: "sm", color: "#64748b" })}>
                {todayStats.next.serviceName}
              </p>
              <p className={css({ fontSize: "sm", color: "#94a3b8", mt: "1" })}>
                {untilLabel(todayStats.next.minutesUntil)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cx(
            cf.dashSurface,
            css({ p: "5", mb: "5", display: "flex", alignItems: "center", gap: "3" }),
          )}
        >
          <div
            className={css({
              w: "10",
              h: "10",
              rounded: "lg",
              bg: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            })}
          >
            <Clock size={18} strokeWidth={1.5} className={css({ color: "#cbd5e1" })} />
          </div>
          <div>
            <p className={css({ fontSize: "sm", fontWeight: "600", color: "#94a3b8" })}>
              Agenda libre
            </p>
            <p className={css({ fontSize: "xs", color: "#cbd5e1" })}>
              No hay turnos próximos pendientes
            </p>
          </div>
        </div>
      )}

      {/* ── Stats de hoy — 3 col ─────────────────────────── */}
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", sm: "repeat(3, 1fr)" },
          gap: "4",
          mb: "8",
        })}
      >
        <StatCard
          label="Confirmados"
          value={String(todayStats.confirmed)}
          sub="hoy confirmados"
          dim={todayStats.confirmed === 0}
        />
        <StatCard
          label="Pendientes"
          value={String(todayStats.pending)}
          sub="por confirmar"
          dim={todayStats.pending === 0}
        />
        <StatCard
          label="Ingresos del día"
          value={dailyRevenue}
          sub="según pagos registrados"
          dim={todayStats.revenue === 0}
        />
      </div>

      {/* ── Selector de período ───────────────────────────── */}
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "4",
          mb: "5",
          flexWrap: "wrap",
        })}
      >
        <p className={css({ fontSize: "sm", color: "#64748b" })}>
          {PERIODS.find((p) => p.value === period)?.label ?? ""}
        </p>
        <div
          className={css({
            display: "inline-flex",
            bg: "#f1f5f9",
            rounded: "xl",
            p: "1",
            gap: "0.5",
            flexShrink: 0,
          })}
        >
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={css({
                px: "3.5",
                py: "1.5",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "500",
                cursor: "pointer",
                border: "none",
                transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), opacity 120ms ease",
                bg: p.value === period ? "white" : "transparent",
                color: p.value === period ? "#0f172a" : "#64748b",
                boxShadow: p.value === period ? "0 1px 3px rgba(15,23,42,0.1)" : "none",
                _hover: { opacity: p.value === period ? 1 : 0.75 },
                _active: { transform: "scale(0.96)" },
              })}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Analytics ────────────────────────────────────── */}
      {!analyticsReady && !analyticsError ? (
        analyticsSkeleton
      ) : analyticsError ? (
        <div
          className={cx(
            cf.dashSurface,
            css({
              p: "5",
              mb: "6",
              display: "flex",
              gap: "3",
              alignItems: "flex-start",
              borderWidth: "1px",
              borderColor: "#fecaca",
              bg: "#fef2f2",
            }),
          )}
        >
          <AlertTriangle
            size={16}
            strokeWidth={2}
            className={css({ color: "#b91c1c", mt: "0.5", flexShrink: 0 })}
          />
          <div>
            <p className={css({ fontSize: "sm", fontWeight: "600", color: "#991b1b", mb: "1" })}>
              No pudimos cargar las métricas del período.
            </p>
            <p className={css({ fontSize: "sm", color: "#94a3b8" })}>
              Probá elegir otro período o recargá la página.
            </p>
          </div>
        </div>
      ) : summary ? (
        <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>

          {/* Featured KPIs: 2 large animated cards */}
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" },
              gap: "4",
            })}
          >
            <FeaturedKpiCard
              label="Ingresos del período"
              prefix="$"
              target={Math.round(summary.totalRevenue)}
              animationKey={`${period}-rev-${summary.totalRevenue}`}
              changeDelta={summary.revenueChange}
              sub={summary.pendingRevenue > 0 ? `+$${summary.pendingRevenue.toFixed(0)} pendientes` : undefined}
              staggerIndex={0}
            />
            <FeaturedKpiCard
              label="Total de turnos"
              target={summary.totalAppointments}
              animationKey={`${period}-appt-${summary.totalAppointments}`}
              changeDelta={summary.bookingsChange}
              sub={`${summary.newClients} nuevos · ${summary.returningClients} recurrentes`}
              staggerIndex={1}
            />
          </div>

          {/* Secondary KPIs: 4 compact cards */}
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: "3",
            })}
          >
            <StatCard
              label="Completados"
              value={String(summary.completedAppointments)}
              sub={`de ${summary.totalAppointments}`}
              dim={summary.completedAppointments === 0}
            />
            <StatCard
              label="Cancelados"
              value={String(summary.cancelledAppointments)}
              dim={summary.cancelledAppointments === 0}
            />
            <div
              className={cx(
                cf.dashSurface,
                css({
                  p: "5",
                  borderWidth: "1px",
                  borderColor: noShowTone(summary.noShowRate).border,
                  bg: noShowTone(summary.noShowRate).bg,
                }),
              )}
            >
              <p
                className={css({
                  fontSize: "11px",
                  fontWeight: "600",
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#94a3b8",
                  mb: "2",
                })}
              >
                Tasa no asistió
              </p>
              <p
                className={css({ fontSize: "2xl", fontWeight: "800", letterSpacing: "-0.02em", lineHeight: "1" })}
                style={{ color: noShowTone(summary.noShowRate).color }}
              >
                {summary.noShowRate.toFixed(1)}%
              </p>
              <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1.5" })}>
                {summary.noShowAppointments} no asistieron
              </p>
            </div>
            <StatCard
              label="Promedio / día"
              value={summary.avgAppointmentsPerDay.toFixed(1)}
              sub="turnos por día"
              dim={summary.avgAppointmentsPerDay === 0}
            />
          </div>

          {/* Charts */}
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", lg: "1fr 1fr" },
              gap: "5",
            })}
          >
            <div className={cx(cf.dashSurfaceStrong, css({ p: "5" }))}>
              <p
                className={css({ fontSize: "sm", fontWeight: "600", color: "#334155", mb: "4" })}
              >
                Turnos por día
              </p>
              <div className={css({ position: "relative", height: "240px" })}>
                <Line
                  data={{
                    labels: lineChart?.labels ?? [],
                    datasets: [
                      {
                        label: "Turnos",
                        data: lineChart?.data ?? [],
                        borderColor: "#0ea5e9",
                        backgroundColor: "rgba(14, 165, 233, 0.08)",
                        fill: true,
                        tension: 0.4,
                        pointRadius: 3,
                        pointBackgroundColor: "#0ea5e9",
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={lineOptions}
                />
              </div>
            </div>

            <div className={cx(cf.dashSurfaceStrong, css({ p: "5" }))}>
              <p
                className={css({ fontSize: "sm", fontWeight: "600", color: "#334155", mb: "4" })}
              >
                Top servicios
              </p>
              <div className={css({ position: "relative", height: "240px" })}>
                <Bar
                  data={{
                    labels: topServices.map((s) => s.serviceName),
                    datasets: [
                      {
                        label: "Reservas",
                        data: topServices.map((s) => Math.round(Number(s.count)) || 0),
                        backgroundColor: "rgba(14, 165, 233, 0.6)",
                        hoverBackgroundColor: "rgba(2, 132, 199, 0.82)",
                        borderRadius: 6,
                      },
                    ],
                  }}
                  options={barOptions}
                />
              </div>
            </div>
          </div>

          {/* Horas pico */}
          {peakHours && peakHours.length > 0 ? (
            <div className={cx(cf.dashSurface, css({ p: "5" }))}>
              <div
                className={css({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: "4",
                })}
              >
                <p className={css({ fontSize: "sm", fontWeight: "600", color: "#334155" })}>
                  Horas pico
                </p>
                <div
                  className={css({
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "1.5",
                    px: "2.5",
                    py: "1",
                    rounded: "full",
                    bg: "#f0f9ff",
                    color: "#0369a1",
                    fontSize: "xs",
                    fontWeight: "600",
                  })}
                >
                  <CalendarCheck size={11} aria-hidden />
                  {peakTopLabel}
                </div>
              </div>
              <ul
                className={css({
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  display: "flex",
                  flexDir: "column",
                  gap: "2.5",
                })}
              >
                {peakHours.map((ph, i) => {
                  const maxCount = peakHours[0].count;
                  const pct = maxCount > 0 ? (ph.count / maxCount) * 100 : 0;
                  return (
                    <li
                      key={`${ph.dayOfWeek}-${ph.hour}`}
                      className={css({ display: "flex", alignItems: "center", gap: "3" })}
                    >
                      <span
                        className={css({
                          fontSize: "xs",
                          color: "#64748b",
                          w: "16",
                          flexShrink: 0,
                          fontVariantNumeric: "tabular-nums",
                        })}
                      >
                        {formatPeakLabel(ph)}
                      </span>
                      <div
                        className={css({
                          flex: 1,
                          h: "5px",
                          rounded: "full",
                          bg: "#f1f5f9",
                          position: "relative",
                          overflow: "hidden",
                        })}
                      >
                        <div
                          className={css({
                            position: "absolute",
                            inset: 0,
                            rounded: "full",
                            transition: shouldReduceMotion
                              ? "none"
                              : "clip-path 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
                          })}
                          style={{
                            background: i === 0 ? "#0ea5e9" : "#7dd3fc",
                            clipPath: `inset(0 ${100 - pct}% 0 0)`,
                          }}
                        />
                      </div>
                      <span
                        className={css({
                          fontSize: "xs",
                          fontWeight: "600",
                          color: "#334155",
                          w: "6",
                          textAlign: "right",
                          flexShrink: 0,
                          fontVariantNumeric: "tabular-nums",
                        })}
                      >
                        {ph.count}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

        </div>
      ) : null}

      {/* ── Actividad reciente ────────────────────────────── */}
      <div className={css({ mt: "8" })}>
        <h2
          className={css({
            fontSize: "sm",
            fontWeight: "600",
            color: "#64748b",
            mb: "4",
            pb: "3",
            borderBottomWidth: "1px",
            borderColor: "#f1f5f9",
          })}
        >
          Actividad reciente
        </h2>
        <div className={cx(cf.dashSurface, css({ p: "5" }))}>
          <DashboardRecentActivityTable
            rows={recent10 as RecentActivityRow[]}
            timezone={timezone}
            agendaIsProfessional={agendaIsProfessional}
          />
        </div>
      </div>

    </DashboardPageShell>
  );
}
